import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PageTemplateContent } from 'app/common/templates-next/page-template'
import { useCurrencySign } from 'app/common/components-next/currency'
import { BannerContent } from 'app/common/components-next/banner'
import Loading from 'app/common/components/Loader'
import usePopup from 'app/common/hooks/usePopup'

import { useCustomerSettings, useCustomerLimitSubmit } from '../dataProviders/customer/hooks'
import { PersonalLimitHeader } from './items/PersonalLimitHeader'
import { CommonLimitOrg } from './items/CommonLimitOrg'
import { PersonalLimit } from './items/PersonalLimit'
import { PersonalLimitEdit } from './items/PersonalLimitEdit'
import { LimitsType } from '../../dataTypes/customer/settings/limit'
import { LimitPerSalePoint } from './items/LimitPerSalePoint'

import { useSellPoints } from '../dataProviders/sellPoints/hooks'
import { PerSalePointContainer } from './items/PerSalePointContainer'
import { LimitEditPerSalePoint } from './items/LimitEditPerSalePoint'
import { useLimits } from './useLimits'

import styles from './Limits.module.scss';
import { MultipleLimitsEditForm } from './items/MultipleLimitsEditForm'


type Props = {
  orgID: string
  customerID: string
  splitLimitEnabled: boolean
}

export const Limits = ({ orgID, customerID, splitLimitEnabled }: Props) => {
  const { t } = useTranslation('cafeteria')

  const {
    settings: customerSettings,
    loading,
    setSettings,
    request,
  } = useCustomerSettings({ params: { customerID } })

  const { sellPoints } = useSellPoints({ params: { customerID, orgID } })
  const currency = useCurrencySign()
  const popup = usePopup()

  const { limit, personalLimit, commonLimit } = useLimits({
    splitLimitEnabled,
    sellPoints,
    ...customerSettings,
  })

  const [maxOfLimit, setMaxOfLimit] = useState(Number.MAX_VALUE)
  const [exceedError, setExceedError] = useState(false)
  const [sending, setSending] = useState(false)

  const changeLimitsInCustomerSettings = useCallback((limits: LimitsType) => {
    setSettings(prevSettings => ({ ...prevSettings, ...limits }))
  }, [setSettings])

  const handleErrorExceed = useCallback((maxLimit: number) => {
    setMaxOfLimit(maxLimit)
    setExceedError(true)
  }, [])

  const { submit: submitLimit } = useCustomerLimitSubmit({
    params: { customerID },
    onSuccess: changeLimitsInCustomerSettings,
    onErrorExceed: handleErrorExceed,
  })

  const handleChangeLimit = useCallback(async (amount: number) => {
    setSending(true)
    const res = await submitLimit({ personalLimit: amount })
    setSending(false)
    if (res) {
      popup(t('settings_limit_submit_success', 'Лимит успешно установлен'))
    }
  }, [submitLimit, popup, t])

  const handleClearLimit = useCallback(async () => {
    setSending(true)
    const res = await submitLimit({ personalLimit: null })
    setSending(false)
    if (res) {
      popup(t('settings_limit_clear_success', 'Лимит успешно cнят'))
    }
  }, [submitLimit, popup, t])

  return (
    <PageTemplateContent className={styles.root} skipPadding>
      <div className={styles.content}>

        {loading && <Loading />}

        {!loading
          && <>
            <PersonalLimitHeader
              limit={limit}
              currency={currency}
            />

            <CommonLimitOrg
              commonLimit={commonLimit}
              personalLimit={customerSettings.personalLimit} // странно конечно почему стили зависят от personalLimit (внутри CommonLimitOrg), но так было изначально
              currency={currency}
            />

            {limit instanceof Array
              && <PerSalePointContainer>
                {limit.map(({ ID, title, value }) => (
                  <LimitPerSalePoint
                    key={ID}
                    currency={currency}
                    targetName={title}
                    value={value}
                  />
                ))}
              </PerSalePointContainer>}

            <div className={styles.bar} />

            <PersonalLimit
              personalLimit={personalLimit}
              currency={currency}
              onClearLimit={handleClearLimit}
            />

            <div className={styles.limitDescription}>
              {t('settings_limit_personal_set_label', 'Установите собственный лимит расходов')}
            </div>

            {!(personalLimit instanceof Array)
              && <PersonalLimitEdit
                currency={currency}
                loading={loading}
                sending={sending}
                onSubmit={handleChangeLimit}
                personalLimit={personalLimit}
                maxOfLimit={maxOfLimit}
                exceedError={exceedError}
                setExceedError={setExceedError}
              />}

            {personalLimit instanceof Array
              && <MultipleLimitsEditForm
                customerID={customerID}
                onSuccess={request}
              >
                {({ onModify, sending: sendingPoints, resetKey, onClearLimit }) => (
                  <PerSalePointContainer>
                    {personalLimit.map(({ ID, title, value }) => (
                      <LimitEditPerSalePoint
                        key={ID}
                        ID={ID}
                        currency={currency}
                        targetName={title}
                        value={value}
                        onClearLimit={onClearLimit}
                        onModify={onModify}
                        loading={loading}
                        sending={sendingPoints}
                        resetKey={resetKey}
                      />
                    ))}
                  </PerSalePointContainer>
                )}
              </MultipleLimitsEditForm>}
          </>}
      </div>

      <BannerContent
        // leaveContainerOnEmpty
        refreshOnMount
        className={styles.bannerContent}
        placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
      />
    </PageTemplateContent>
  )
}
